import { useCallback, useEffect, useState } from 'react';

export interface ConductricsClientApi {
  getDecision: (decisionId: string, callback: (result: unknown) => void) => void;
  sendGoal: (goalId: string, value?: number) => void;
  exec: (
    commands: Array<{ a?: string; g?: string; [key: string]: unknown }>,
    callback: (err: Error | null, res?: { sels?: Record<string, string>; [key: string]: unknown }) => void
  ) => void;
}

// Augment globalThis with Conductrics
declare global {
  var Conductrics:
    | {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ClientApi: new () => ConductricsClientApi;
      }
    | undefined;
}

interface ConductricsApi {
  getDecision: (decisionId: string, callback: (result: unknown) => void) => void;
  sendGoal: (goalId: string, value?: number) => void;
  exec: (
    commands: Array<{ a?: string; g?: string; [key: string]: unknown }>,
    callback: (err: Error | null, res?: { sels?: Record<string, string>; [key: string]: unknown }) => void
  ) => void;
}

interface UseConductricsReturn {
  isLoaded: boolean;
  error: Error | null;
  getVariation: <T extends string>(agentCode: string) => Promise<T>;
  sendGoalEvent: (goalCode: string) => void;
}

const SCRIPT_CHECK_INTERVAL_MS = 100;
const MAX_ATTEMPTS = 10; // 10 seconds with 100ms intervals
const DEFAULT_VARIATION = 'A';

/**
 * Custom hook to initialize and access the Conductrics API.
 * Waits for the Conductrics script to load and provides the API instance.
 *
 * @example
 * const { getVariation, sendGoalEvent, isLoaded } = useConductrics();
 *
 * useEffect(() => {
 *   const fetchVariation = async () => {
 *     if (isLoaded) {
 *       const variation = await getVariation<'A' | 'B'>('a-cnKT47g78UG0');
 *       setSelectedVariation(variation);
 *     }
 *   };
 *   fetchVariation();
 * }, [isLoaded, getVariation]);
 *
 * const handleButtonClick = () => {
 *   sendGoalEvent('g-button-click');
 * };
 */
export const useConductrics = (): UseConductricsReturn => {
  const [api, setApi] = useState<ConductricsApi | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Only run in browser environment
    if (globalThis.window === undefined) {
      return;
    }

    // If Conductrics is already loaded, initialize immediately
    if (globalThis.Conductrics) {
      try {
        const conductrics = new globalThis.Conductrics.ClientApi();
        setApi(conductrics);
        setIsLoaded(true);
      } catch (err) {
        const initError = err instanceof Error ? err : new Error('Failed to initialize Conductrics');
        setError(initError);
      }
      return;
    }

    // Poll for Conductrics script loading with timeout
    let attempts = 0;

    const checkInterval = setInterval(() => {
      attempts++;

      if (globalThis.Conductrics) {
        clearInterval(checkInterval);
        try {
          const clientApi = new globalThis.Conductrics.ClientApi();
          setApi(clientApi);
          setIsLoaded(true);
        } catch (err) {
          const initError = err instanceof Error ? err : new Error('Failed to initialize Conductrics');
          setError(initError);
        }
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(checkInterval);
        const timeoutError = new Error('Conductrics script failed to load within timeout');
        setError(timeoutError);
      }
    }, SCRIPT_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  const getVariation = useCallback(
    async <T extends string>(agentCode: string): Promise<T> => {
      if (!isLoaded || !api) {
        // Return default variation when API is not loaded
        return DEFAULT_VARIATION as T;
      }

      return new Promise(resolve => {
        try {
          api.exec([{ a: agentCode }], (err, res) => {
            if (err) {
              console.warn('Error fetching variation from Conductrics, use default variation:', err);
              resolve(DEFAULT_VARIATION as T);
              return;
            }

            if (!res?.sels) {
              console.warn('Invalid response from Conductrics');
              resolve(DEFAULT_VARIATION as T);
              return;
            }

            const selectedVariation = res.sels[agentCode];
            resolve(selectedVariation as T);
          });
        } catch (err) {
          const execError = err instanceof Error ? err : new Error('Failed to execute Conductrics variation selection');
          // Return default variation on exception
          console.warn('Exception in getVariation:', execError);
          resolve(DEFAULT_VARIATION as T);
        }
      });
    },
    [isLoaded, api]
  );

  const sendGoalEvent = useCallback(
    (goalCode: string) => {
      if (!api) {
        console.warn('Conductrics API not loaded. Cannot send goal:', goalCode);
        return;
      }

      try {
        api.exec([{ g: goalCode }], err => {
          if (err) {
            console.warn('Failed to send goal to Conductrics:', err);
          }
        });
      } catch (err) {
        const execError = err instanceof Error ? err : new Error('Failed to send goal to Conductrics');
        console.warn('Exception when sending goal to Conductrics:', execError);
      }
    },
    [api]
  );

  return { isLoaded, error, getVariation, sendGoalEvent };
};
